import {
  inputClassName,
  labelClassName,
  linkClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../features/ui/classes';

export { ConfirmDialog } from './ConfirmDialog';
export { SyncRunBanner } from './SyncRunBanner';
export { StatusBanner, PageLoading } from './StatusBanner';
export { showStatusToast } from './statusToast';

export function StatCard({ label, value, detail, icon: Icon, status = 'neutral' }) {
  const statusClass = {
    success: 'crm-status-dot-success',
    warning: 'crm-status-dot-warning',
    neutral: 'crm-status-dot-neutral',
  }[status] || 'crm-status-dot-neutral';

  return (
    <article className="crm-stat-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`crm-status-dot ${statusClass}`} aria-hidden="true" />
            <span className="crm-text-muted block text-[11px] font-semibold uppercase tracking-wider">
              {label}
            </span>
          </div>
          <strong className="mt-2.5 block truncate text-2xl font-semibold tracking-tight">{value}</strong>
          {detail ? <p className="crm-text-muted mt-2 text-[13px] leading-relaxed">{detail}</p> : null}
        </div>
        {Icon ? (
          <span className="crm-stat-icon" aria-hidden="true">
            <Icon className="h-5 w-5" />
          </span>
        ) : null}
      </div>
    </article>
  );
}

export function PanelHeader({ title, subtitle, action }) {
  return (
    <div className="crm-panel-header">
      <div>
        <h2 className="crm-panel-title">{title}</h2>
        {subtitle ? <p className="crm-panel-subtitle">{subtitle}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function QuickAction({ to, icon: Icon, title, description, onClick, as: Component = onClick ? 'button' : 'div' }) {
  const content = (
    <>
      <span className="crm-quick-action-icon">
        {Icon ? <Icon className="h-[18px] w-[18px]" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block text-sm font-semibold tracking-tight text-[rgb(var(--crm-text))]">
          {title}
        </strong>
        {description ? (
          <span className="crm-text-muted mt-1 block text-[12px] leading-snug line-clamp-2">
            {description}
          </span>
        ) : null}
      </span>
      <svg
        className="mt-1 h-4 w-4 shrink-0 text-[rgb(var(--crm-text-muted))] transition group-hover:translate-x-0.5 group-hover:text-[rgb(var(--crm-accent))]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M9 6L15 12L9 18" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </>
  );

  if (to) {
    return (
      <a href={to} className="crm-quick-action group no-underline">
        {content}
      </a>
    );
  }

  if (Component === 'button') {
    return (
      <button type="button" className="crm-quick-action group w-full" onClick={onClick}>
        {content}
      </button>
    );
  }

  return <div className="crm-quick-action group">{content}</div>;
}

export function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon ? (
        <span className="crm-stat-icon mt-0.5 h-9 w-9 shrink-0">
          <Icon className="h-4 w-4" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <dt className="crm-text-muted text-[11px] font-semibold uppercase tracking-wider">{label}</dt>
        <dd className="mt-1 font-medium">{value}</dd>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        {subtitle ? <p className="crm-text-muted mt-1 text-sm">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function Badge({ children, tone = 'default' }) {
  const tones = {
    default: 'border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] text-[rgb(var(--crm-text))]',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    warning: 'border-amber-200 bg-amber-50 text-amber-900',
    danger: 'border-red-200 bg-red-50 text-red-800',
    info: 'border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-table-head))] text-[rgb(var(--crm-accent-text))]',
  };

  return (
    <span
      className={`inline-flex min-h-[1.65rem] items-center rounded-full border px-2 text-xs font-semibold ${tones[tone] || tones.default}`}
    >
      {children}
    </span>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div className="crm-panel flex flex-col items-center justify-center border-dashed px-6 py-14 text-center">
      <svg
        className="h-16 w-16 opacity-30"
        viewBox="0 0 64 64"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="10" y="14" width="44" height="36" rx="4" />
        <path d="M22 28H42" strokeLinecap="round" />
        <path d="M22 36H36" strokeLinecap="round" />
      </svg>
      <h3 className="mt-4 text-base font-semibold">{title}</h3>
      <p className="crm-text-muted mt-2 max-w-md text-sm leading-relaxed">{description}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function SandboxBadge({ mode = 'sandbox' }) {
  const isProduction = mode === 'production';

  return (
    <span
      className={[
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        isProduction
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
          : 'border-violet-200 bg-violet-50 text-violet-800',
      ].join(' ')}
    >
      {isProduction ? 'SP-API Production' : 'SP-API Sandbox'}
    </span>
  );
}

export function DataTable({ columns, rows, emptyMessage, loading }) {
  return (
    <div className={`${panelClassName} overflow-x-auto`}>
      <table className="w-full min-w-[48rem] border-collapse">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key || column} className={tableHeadClassName}>
                {column.label || column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className={`${tableCellClassName} crm-text-muted text-center`}>
                Loading...
              </td>
            </tr>
          ) : rows.length ? (
            rows
          ) : (
            <tr>
              <td colSpan={columns.length} className={`${tableCellClassName} crm-text-muted text-center`}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export {
  inputClassName,
  labelClassName,
  linkClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
};
